import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MessagingRecipient {
  id: string;
  name: string;
  email: string | null;
}

export interface TeamMessage {
  id: string;
  thread_id: string;
  sender_type: string;
  sender_user_id: string | null;
  sender_name: string | null;
  body: string;
  read_at: string | null;
  created_at: string;
}

interface TeamThread {
  id: string;
}

/**
 * Team members an owner/supervisor can start a message thread with.
 * Scoped to the active org; RLS restricts this to the caller's own org anyway.
 */
export function useMessagingRecipients(orgId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['messaging-recipients', orgId],
    enabled: Boolean(orgId) && enabled,
    queryFn: async (): Promise<MessagingRecipient[]> => {
      const { data, error } = await supabase
        .from('team_members')
        .select('id, name, email')
        .eq('organization_id', orgId!)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as MessagingRecipient[];
    },
  });
}

/**
 * Loads (or lets you start) the single message thread between the org and one
 * team member, plus its messages. `sendMessage` lazily creates the thread on the
 * first message so an empty recipient never leaves an orphan thread behind.
 */
export function useMessageThread(params: {
  orgId: string | null | undefined;
  teamMemberId: string | null | undefined;
  senderType: 'org' | 'cleaner';
  senderUserId: string | null | undefined;
  senderName: string | null;
  canWrite: boolean;
}) {
  const { orgId, teamMemberId, senderType, senderUserId, senderName, canWrite } = params;
  const queryClient = useQueryClient();

  const threadQuery = useQuery({
    queryKey: ['team-message-thread', orgId, teamMemberId],
    enabled: Boolean(orgId) && Boolean(teamMemberId),
    queryFn: async (): Promise<TeamThread | null> => {
      const { data, error } = await supabase
        .from('team_message_threads')
        .select('id')
        .eq('organization_id', orgId!)
        .eq('team_member_id', teamMemberId!)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  const threadId = threadQuery.data?.id ?? null;

  const messagesQuery = useQuery({
    queryKey: ['team-messages', threadId],
    enabled: Boolean(threadId),
    queryFn: async (): Promise<TeamMessage[]> => {
      const { data, error } = await supabase
        .from('team_messages')
        .select('id, thread_id, sender_type, sender_user_id, sender_name, body, read_at, created_at')
        .eq('thread_id', threadId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as TeamMessage[];
    },
  });

  const sendMessage = useMutation({
    mutationFn: async (body: string) => {
      if (!canWrite) throw new Error('Read-only support mode: tenant changes are disabled.');
      const trimmed = body.trim();
      if (!trimmed) throw new Error('Message cannot be empty');
      if (!orgId || !teamMemberId) throw new Error('No recipient selected');

      let activeThreadId = threadId;
      if (!activeThreadId) {
        const { data, error } = await supabase
          .from('team_message_threads')
          .insert({ organization_id: orgId, team_member_id: teamMemberId, created_by: senderUserId ?? null })
          .select('id')
          .single();
        if (error) throw error;
        activeThreadId = data.id;
      }

      const { error: messageError } = await supabase.from('team_messages').insert({
        thread_id: activeThreadId,
        organization_id: orgId,
        sender_type: senderType,
        sender_user_id: senderUserId ?? null,
        sender_name: senderName,
        body: trimmed,
      });
      if (messageError) throw messageError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-message-thread', orgId, teamMemberId] });
      if (threadId) {
        queryClient.invalidateQueries({ queryKey: ['team-messages', threadId] });
      }
    },
  });

  // Marks messages from the other side as read once the thread is viewed, so the
  // unread badge clears. Only the incoming, still-unread messages are touched.
  const markRead = useMutation({
    mutationFn: async (messageIds: string[]) => {
      if (!canWrite) return;
      if (messageIds.length === 0) return;
      const { error } = await supabase
        .from('team_messages')
        .update({ read_at: new Date().toISOString() })
        .in('id', messageIds);
      if (error) throw error;
    },
    onSuccess: () => {
      if (threadId) {
        queryClient.invalidateQueries({ queryKey: ['team-messages', threadId] });
      }
    },
  });

  const messages = messagesQuery.data ?? [];
  const unreadIncomingIds = messages
    .filter((m) => m.sender_type !== senderType && !m.read_at)
    .map((m) => m.id);
  const unreadKey = unreadIncomingIds.join(',');
  const markReadMutate = markRead.mutate;
  const markReadPending = markRead.isPending;

  useEffect(() => {
    if (!canWrite || !threadId || !unreadKey || markReadPending) return;
    markReadMutate(unreadKey.split(','));
  }, [canWrite, threadId, unreadKey, markReadPending, markReadMutate]);

  return {
    messages,
    isLoading: threadQuery.isLoading || (Boolean(threadId) && messagesQuery.isLoading),
    hasThread: Boolean(threadId),
    sendMessage,
  };
}
